import { useReactFlow } from '@xyflow/react';
import { FinalidadeEstado, type ProcessoCartaoDto, type QuadroDto } from '@nexital/shared';
import { useQuadro } from '../../application/dependencias';
import type { Ponto } from '../../domain/camera';
import { proximaPosicaoLivre, receberCartao } from '../../domain/grupos';
import { decidirSoltura, pontoNaArea, type DestinoSoltura } from '../../domain/soltura';
import { useAcoesEstado } from './AcoesEstado';
import { ALTURA_CARTAO, LARGURA_CARTAO, idCartao, type NoCartaoTipo } from './montar-nos';

const TAMANHO_CARTAO = { largura: LARGURA_CARTAO, altura: ALTURA_CARTAO };
/** Tipo do dado arrastado da bandeja Sem estado para o quadro (arrastar e soltar do HTML). */
export const ARRASTE_SEM_ESTADO = 'application/x-nexital-processo';

type EventoPonteiro = MouseEvent | TouchEvent | React.MouseEvent | React.DragEvent;

export function pontoDoEvento(evento: EventoPonteiro): Ponto | null {
  if ('clientX' in evento) return { x: evento.clientX, y: evento.clientY };
  const toque = evento.changedTouches[0];
  return toque ? { x: toque.clientX, y: toque.clientY } : null;
}

export function pontoSobre(elemento: Element | null, ponto: Ponto | null, margem = 0) {
  return pontoNaArea(elemento?.getBoundingClientRect(), ponto, margem);
}

const semEstadoDe = (dados: QuadroDto) => dados.estados.find((e) => e.finalidade === FinalidadeEstado.SemEstado)?.uuid ?? '';

/**
 * O que acontece quando um cartão é solto (RQ-EST-003, RQ-EST-005, RQ-EST-009, RQ-EST-010): a decisão é pura
 * (domain/soltura.ts); aqui ela vira gravação, crescimento de grupos e caixas de confirmação.
 */
export function useSoltura(voltar: (idNo: string) => void) {
  const dados = useQuadro((s) => s.dados);
  const moverProcesso = useQuadro((s) => s.moverProcesso);
  const acoes = useAcoesEstado();
  const { getInternalNode, screenToFlowPosition } = useReactFlow();

  const aplicar = async (quadro: QuadroDto, processo: ProcessoCartaoDto, destino: DestinoSoltura, absoluta: Ponto) => {
    const voltarCartao = () => voltar(idCartao(processo.uuid));
    if (destino.tipo === 'lixeira') return acoes.enviarLixeira(processo, voltarCartao);
    if (destino.tipo === 'sem-estado') return acoes.enviarSemEstado(processo, absoluta, voltarCartao);
    if (destino.tipo === 'nenhum') return voltarCartao();
    // O grupo cresce e empurra vizinhos se preciso; essa geometria grava junto com a ação do cartão.
    const { posicao, alterados: grupos } = receberCartao(quadro.grupos, destino.grupoUuid, destino.posicao, TAMANHO_CARTAO);
    if (destino.tipo === 'mesmo-grupo') return moverProcesso(processo.uuid, posicao, grupos);
    await acoes.mudar({ processoUuid: processo.uuid, estadoUuid: destino.estadoUuid, posicao, grupos }, voltarCartao);
  };

  /** Cartão do quadro solto. Com vários cartões arrastados juntos, Lixeira e Sem estado não se aplicam: eles voltam. */
  const soltarCartao = async (no: NoCartaoTipo, sobreLixeira: boolean, varios: boolean) => {
    if (!dados) return;
    const absoluta = getInternalNode(no.id)?.internals.positionAbsolute ?? no.position;
    const destino = decidirSoltura({
      cartao: { ...absoluta, ...TAMANHO_CARTAO },
      sobreLixeira,
      estadoAtualUuid: no.data.processo.estadoUuid,
      semEstadoUuid: semEstadoDe(dados),
      grupos: dados.grupos,
    });
    const paraGrupo = destino.tipo === 'mesmo-grupo' || destino.tipo === 'outro-grupo';
    return aplicar(dados, no.data.processo, varios && !paraGrupo ? { tipo: 'nenhum' } : destino, absoluta);
  };

  /** Cartão arrastado da bandeja Sem estado e solto no quadro: só vale sobre um grupo. */
  const soltarDaBandeja = (evento: React.DragEvent) => {
    const processo = dados?.processos.find((p) => p.uuid === evento.dataTransfer.getData(ARRASTE_SEM_ESTADO));
    if (!dados || !processo) return;
    evento.preventDefault();
    const noQuadro = screenToFlowPosition({ x: evento.clientX, y: evento.clientY });
    const cartao = { x: noQuadro.x - LARGURA_CARTAO / 2, y: noQuadro.y - 24, ...TAMANHO_CARTAO };
    const destino = decidirSoltura({ cartao, sobreLixeira: false, estadoAtualUuid: processo.estadoUuid, semEstadoUuid: semEstadoDe(dados), grupos: dados.grupos });
    if (destino.tipo === 'outro-grupo') void aplicar(dados, processo, destino, cartao);
  };

  /** Alternativa ao arraste (teclado): leva o cartão para o grupo do estado, abaixo dos cartões que já estão lá. */
  const enviarParaGrupo = (processoUuid: string, estadoUuid: string) => {
    const processo = dados?.processos.find((p) => p.uuid === processoUuid);
    const grupo = dados?.grupos.find((g) => g.estadoUuid === estadoUuid);
    if (!dados || !processo || !grupo) return;
    const ocupados = dados.processos.filter((p) => p.estadoUuid === estadoUuid).map((p) => ({ x: p.x, y: p.y, ...TAMANHO_CARTAO }));
    const destino = { tipo: 'outro-grupo' as const, grupoUuid: grupo.uuid, estadoUuid, posicao: proximaPosicaoLivre(ocupados) };
    void aplicar(dados, processo, destino, destino.posicao);
  };

  /** Props do elemento do quadro que recebe cartões arrastados da bandeja Sem estado. */
  const receberDaBandeja = {
    onDragOver: (evento: React.DragEvent) => {
      if (evento.dataTransfer.types.includes(ARRASTE_SEM_ESTADO)) evento.preventDefault();
    },
    onDrop: soltarDaBandeja,
  };

  return { soltarCartao, receberDaBandeja, enviarParaGrupo };
}
